import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import styles from './creditLimitModal.module.scss'

interface CreditLimitModalProps {
  dailyLimit?: number
  isOpen: boolean
  onClose: () => void
  onContactSupport: () => void
  resetsAt?: string
}

export const CreditLimitModal: FC<CreditLimitModalProps> = ({
  dailyLimit = 100,
  isOpen,
  onClose,
  onContactSupport,
  resetsAt,
}) => {
  const [resetTimeDisplay, setResetTimeDisplay] = useState('')

  useEffect(() => {
    const calculateTimeUntilReset = () => {
      if (!resetsAt) {
        setResetTimeDisplay('24 hours')
        return
      }

      const now = new Date()
      const resetTime = new Date(resetsAt)

      const diffMs = resetTime.getTime() - now.getTime()
      if (diffMs <= 0) {
        setResetTimeDisplay('soon')
        return
      }

      const hours = Math.floor(diffMs / (1000 * 60 * 60))
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

      if (hours > 0) {
        setResetTimeDisplay(`${hours}h ${minutes}m`)
      } else {
        setResetTimeDisplay(`${minutes} minutes`)
      }
    }

    calculateTimeUntilReset()

    // Update every minute
    const interval = setInterval(calculateTimeUntilReset, 60000)

    return () => clearInterval(interval)
  }, [resetsAt])

  if (!isOpen) return null

  return createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <img alt="Hapttic" className={styles.logo} src="/logo.png" />

        <div className={styles.content}>
          <div className={styles.iconWrapper}>
            <svg
              className={styles.warningIcon}
              fill="none"
              height="92"
              viewBox="0 0 80 92"
              width="80"
            >
              <path
                d="M34.5359 7.99999C37.0755 3.59999 43.5359 3.59999 46.0755 7.99999L77.3205 62C79.8601 66.4 76.6301 72 71.5509 72H9.06091C3.98171 72 0.751709 66.4 3.29131 62L34.5359 7.99999Z"
                fill="#FF6833"
              />
              <path
                d="M40.3057 28V44"
                stroke="white"
                strokeLinecap="round"
                strokeWidth="6"
              />
              <circle cx="40.3057" cy="56" fill="white" r="4" />
            </svg>
          </div>

          <h2 className={styles.title}>Daily AI Credit Limit Reached</h2>

          <p className={styles.description}>
            You&apos;ve used all {dailyLimit} AI credits allocated for today.
            Your credits will automatically reset in {resetTimeDisplay}.{' '}
            <span className={styles.highlight}>
              To continue using the AI Assistant now, contact support
            </span>
          </p>

          <div className={styles.actions}>
            <button className={styles.discardButton} onClick={onClose}>
              Discard
            </button>
            <button className={styles.contactButton} onClick={onContactSupport}>
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
